<template>
  <VcModal :title="title" max-width="60rem" hide-actions is-mobile-fullscreen test-id="extended-address-modal">
    <template #default="{ close }">
      <!-- Loading state while countries are being fetched -->
      <div v-if="isInitializing" class="flex min-h-[200px] items-center justify-center p-8">
        <VcLoaderOverlay />
      </div>

      <!-- Error state -->
      <div v-else-if="initError" class="p-8 text-center">
        <p class="text-[--color-danger-500]">{{ initError }}</p>

        <VcButton class="mt-4" variant="outline" @click="close">
          {{ $t("common.buttons.close") }}
        </VcButton>
      </div>

      <!-- Form content -->
      <ExtendedAddressForm
        v-else
        ref="formRef"
        :address="addressData"
        :extension="extensionData"
        :countries="countries"
        :disabled="loading"
        :address-purpose="addressPurpose"
        @save="handleSave"
      >
        <template #append="{ dirty, valid }">
          <div class="flex flex-wrap items-center justify-between gap-4 pb-6 pt-4 *:max-xs:flex-1">
            <VcButton min-width="8rem" color="secondary" variant="outline" @click="close">
              {{ $t("common.buttons.cancel") }}
            </VcButton>

            <VcButton min-width="8rem" :disabled="!dirty || !valid" :loading="loading" type="submit">
              {{ isEdit ? $t("common.buttons.save") : $t("common.buttons.create") }}
            </VcButton>
          </div>
        </template>
      </ExtendedAddressForm>
    </template>
  </VcModal>
</template>

<script setup lang="ts">
import { computed, onMounted, shallowRef, ref } from "vue";
import { useCountries } from "@/core/composables";
import { Logger } from "@/core/utilities";
import { getAddressExtensionByAddressId } from "../api";
import { AddressSource } from "../types";
import ExtendedAddressForm from "./extended-address-form.vue";
import type { IAddressExtensionFormState } from "../types";
import type { MemberAddressType } from "@/core/api/graphql/types";

interface IEmits {
  (event: "result", address: MemberAddressType, extensionState: IAddressExtensionFormState): void;
}

interface IProps {
  address?: MemberAddressType;
  loading?: boolean;
  addressPurpose?: "shipping" | "billing" | "both" | "general";
  addressSource?: AddressSource;
}

const emit = defineEmits<IEmits>();
const props = withDefaults(defineProps<IProps>(), {
  addressPurpose: "general",
  addressSource: AddressSource.Member,
});

const { countries, loadCountries } = useCountries();

const formRef = ref<InstanceType<typeof ExtendedAddressForm>>();
// Initialize address data immediately from props - strip __typename to avoid GraphQL parsing issues
const addressData = ref<MemberAddressType | undefined>(
  props.address
    ? {
        id: props.address.id,
        name: props.address.name,
        organization: props.address.organization,
        firstName: props.address.firstName,
        lastName: props.address.lastName,
        line1: props.address.line1,
        line2: props.address.line2,
        city: props.address.city,
        countryCode: props.address.countryCode,
        countryName: props.address.countryName,
        regionId: props.address.regionId,
        regionName: props.address.regionName,
        postalCode: props.address.postalCode,
        isDefault: props.address.isDefault,
        isFavorite: props.address.isFavorite,
        phone: props.address.phone,
        email: props.address.email,
        description: props.address.description,
        addressType: props.address.addressType,
      }
    : undefined,
);
// Use shallowRef to ensure Vue detects reference changes
const extensionData = shallowRef<IAddressExtensionFormState | undefined>(undefined);

// Initialization state
const isInitializing = ref(true);
const initError = ref<string | null>(null);

const isEdit = computed(() => !!props.address?.id);
const title = computed(() => (isEdit.value ? "ویرایش آدرس" : "آدرس جدید"));

onMounted(async () => {
  isInitializing.value = true;
  initError.value = null;

  try {
    // Load countries first
    if (!countries.value.length) {
      await loadCountries();
    }

    // Verify countries loaded
    if (!countries.value.length) {
      initError.value = "Failed to load countries data";
      Logger.error("ExtendedAddressModal", "Countries failed to load");
      return;
    }

    // Load extension data if editing
    if (props.address?.id) {
      try {
        Logger.debug("ExtendedAddressModal", `Loading extension for address ${props.address.id}, source: ${props.addressSource}`);
        
        // Try to load from the specified source first
        let ext = await getAddressExtensionByAddressId(props.address.id, props.addressSource);

        Logger.debug("ExtendedAddressModal", "Extension data loaded:", ext);

        // If Cart source didn't find data, try Member source as fallback
        // (This handles the case where address was selected from saved addresses but Cart extension wasn't saved yet)
        if (!ext && props.addressSource === AddressSource.Cart) {
          Logger.debug("ExtendedAddressModal", "Cart source had no data, trying Member source");
          ext = await getAddressExtensionByAddressId(props.address.id, AddressSource.Member);
        }

        // Check if extension has meaningful data (id exists means it was actually saved)
        // API may return an object with all null values for addresses without extensions
        if (ext) {
          Logger.debug("ExtendedAddressModal", "Extension has data, ext.id:", ext.id);
          
          extensionData.value = {
            guild: ext.guild || "",
            storeName: ext.storeName || "",
            latitude: ext.latitude ?? null,
            longitude: ext.longitude ?? null,
            personType: ext.personType ?? null,
            naturalPersonName: ext.naturalPersonName || "",
            naturalPersonFamily: ext.naturalPersonFamily || "",
            naturalPersonNationalCode: ext.naturalPersonNationalCode || "",
            nationalCardAssetId: ext.nationalCardAssetId ?? null,
            legalPersonName: ext.legalPersonName || "",
            legalPersonNationalCode: ext.legalPersonNationalCode || "",
            legalPersonEconomicalCode: ext.legalPersonEconomicalCode || "",
            businessLicenseAssetId: ext.businessLicenseAssetId ?? null,
          };
          
          Logger.debug("ExtendedAddressModal", "extensionData set to:", extensionData.value);
        } else {
          Logger.warn("ExtendedAddressModal", "No extension data found for this address");
        }
      } catch (extError) {
        // Log but don't crash - extension data is optional
        Logger.error("ExtendedAddressModal", "Failed to load extension data:", extError);
      }
    }
  } catch (error) {
    Logger.error("ExtendedAddressModal", "Error in onMounted:", error);
    initError.value = "Failed to initialize form";
  } finally {
    isInitializing.value = false;
  }
});

function handleSave(address: MemberAddressType, extension: IAddressExtensionFormState): void {
  emit("result", address, extension);
}
</script>
